import { useEffect, useState } from 'react';
import { compose } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import Error from 'core/errors/Error';
import withAppState from 'hoc/withAppState';
import PageContent from 'layouts/PageContent';
import Transaction from 'components/transactions/Transaction';

import { buildRoute } from 'routes';
import Button from 'core/buttons/Button';
import {
  resetApproveTransaction,
  resetGetTransactionFailure,
  resetRejectTransaction,
  resetUpdateRejectedToCompleted,
} from 'app/features/transactions/transactionSlice';
import withRole from 'hoc/withRole';
import acl from 'app/config/acl';
import { Box, Grid } from '@mui/material';
import ViewTransactionDocument from './ViewTransactionDocument';
import ViewTransactionDetailDocument from './ViewTransactionDetailDocument';
import SpringModal from 'core/modal/SpringModal';
import UpdateRejectedToCompletedForm from 'components/transactions/UpdateRejectedToCompletedForm';
import {
  updateRejectedToCompleted,
  getTransaction,
} from 'app/features/transactions/transactionSaga';
import { getTransactionsLog } from 'app/features/transactions/transactionLogSaga';
import { transactionStatuses } from 'app/config/TransactionStatus';

import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';

function ViewTransaction() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isError,
    errorCode,
    errorMessage,
    isLoading,
    data: transaction,
  } = useSelector((state) => state.transactions.show);

  const { isLoading: isTransactionLogLoading, data: transactionLogData } = useSelector(
    (state) => state.transactionLogs,
  );

  const { isSuccess } = useSelector((state) => state.transactions.updateRejectedToCompleted);
  const { isSuccess: isApproveSuccess } = useSelector((state) => state.transactions.approve);
  const { isSuccess: isRejectSuccess } = useSelector((state) => state.transactions.reject);

  const [openModal, setOpenModal] = useState(false);
  const [customerId, setCustomerId] = useState(null);

  const resolvedCustomerId = transaction?.user_id ?? customerId;

  useEffect(() => {
    setOpenModal(false);
    dispatch(getTransaction(params.transactionId));
    dispatch(getTransactionsLog(params.transactionId));
    dispatch(resetUpdateRejectedToCompleted());
  }, [dispatch, params.transactionId]);

  useEffect(() => {
    if (isSuccess) {
      setOpenModal(false);
      dispatch(getTransaction(params.transactionId));
      dispatch(resetUpdateRejectedToCompleted());
      dispatch(getTransactionsLog(params.transactionId));
    }
  }, [isSuccess, dispatch, params.transactionId]);

  useEffect(() => {
    if (isApproveSuccess) {
      dispatch(getTransaction(params.transactionId));
      dispatch(getTransactionsLog(params.transactionId));
      dispatch(resetApproveTransaction());
    }
  }, [isApproveSuccess, dispatch, params.transactionId]);

  useEffect(() => {
    if (isRejectSuccess) {
      dispatch(getTransaction(params.transactionId));
      dispatch(getTransactionsLog(params.transactionId));
      dispatch(resetRejectTransaction());
    }
  }, [isRejectSuccess, dispatch, params.transactionId]);

  const handleOpenModal = () => setOpenModal(true);

  const handleSubmit = (data) => {
    dispatch(
      updateRejectedToCompleted({
        ...data,
        transactionId: transaction?.order_id,
      }),
    );
  };

  if (isError)
    return (
      <Error
        status={errorCode}
        message={errorMessage}
        onRetry={() => dispatch(resetGetTransactionFailure())}
      />
    );

  const isRejectedStatus =
    transaction?.status?.toLowerCase().trim() === transactionStatuses.rejected.toLowerCase().trim();

  return (
    <PageContent
      title="Transaction Details"
      rightTopEndContent={
        <Box display="flex" gap={2}>
          {resolvedCustomerId && (
            <Button
              onClick={() =>
                navigate(buildRoute('customers.show', resolvedCustomerId, 'tab=customer-details'), {
                  state: { activeTab: 'customer-details' },
                })
              }
            >
              History
            </Button>
          )}{' '}
          <Button onClick={() => navigate(buildRoute('transactions.index'))}>
            All Transaction
          </Button>
          {!isLoading && isRejectedStatus && (
            <HasPermission permission={permissions.UPDATE_BNB_REJECTED_TXN}>
              <Button onClick={handleOpenModal}>Update Status</Button>
            </HasPermission>
          )}
        </Box>
      }
    >
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={9}
          sx={{
            overflowY: {
              xs: 'visible',
              md: 'auto',
            },
            maxHeight: '100vh',
          }}
        >
          <Transaction
            transactionId={params.transactionId}
            transaction={transaction}
            isLoading={isLoading}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
          sx={{
            position: 'sticky',
            top: 16, // distance from top
            height: 'calc(100vh - 32px)',
            overflowY: 'auto',
            paddingLeft: 2,
          }}
        >
          <ViewTransactionDetailDocument
            transactionId={params.transactionId}
            onCustomerId={setCustomerId}
            transaction={transaction}
            transactionLogs={transactionLogData}
            isTransactionLoading={isTransactionLogLoading}
          />
        </Grid>
      </Grid>

      <SpringModal
        header="Update Transaction Status (Rejected to Completed)"
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        enableMaximizeButton={false}
        enableDefaultPadding
        sx={{ maxWidth: '600px' }}
      >
        <UpdateRejectedToCompletedForm
          onSubmit={handleSubmit}
          onCancel={() => setOpenModal(false)}
        />
      </SpringModal>
    </PageContent>
  );
}

export default compose(
  withAppState,
  withRole([acl.SUPERADMIN, acl.ADMIN, acl.USER]),
)(ViewTransaction);
